import JWT from 'jsonwebtoken';
import { v4 as UUID } from 'uuid';
import Analytics from '../analytics';
import Environment from '../environment';
import HwpTvApi from '../hwpTvApi';
import { getLogger } from '../logging/logger';
import Comcast from '../platform/comcast';
import { getSearchParam } from '../util/utilities';
import StorageManager from './persistentStorageManager';

const REGEX_UUID = /^\w{8}-\w{4}-\w{4}-\w{4}-\w{12}$/i;
const ID_STORAGE_NAME = 'hwptvId';

const logger = getLogger('AppAuthManager');

function intToHex(nr) {
	return nr.toString(16).padStart(2, '0');
}

function getRandomString(bytes = 30) {
	const randomValues = new Uint8Array(bytes);
	window.crypto.getRandomValues(randomValues);
	return Array.from(randomValues).map(intToHex).join('');
}

function getAuth() {
	logger.log('[getAuth]');

	return import('../hwptvAmplify').then((Auth) => Auth.default);
}

class AppAuthManager {
	/**
	 * Returns promise that is called when the AppAuthManager is ready to be used by other modules
	 */
	isReady() {
		logger.log(`[${this.isReady.name}]`);

		if (!this.readyPromise) {
			this.readyPromise = new Promise((resolve, reject) => {
				this.onReadyResolve = resolve;
				this.onReadyReject = reject;
			});
		}

		return this.readyPromise;
	}

	initialize(options = {}) {
		logger.log(`[${this.initialize.name}]`, { ...options });

		this.cognitoCheckDisabled = true;
		this.accountVariant = Environment.isKidsVariant() ? 'kids' : undefined;

		Object.assign(this, options);

		this.isReady(); // Make sure ready promise is created

		HwpTvApi.off('error');
		HwpTvApi.on('error', this.onApiError.bind(this));

		return Promise.resolve(this.findUserCredentials())
			.then((overrideId) => {
				if (overrideId) {
					return this.initOverrideAuth(overrideId);
				}

				if (Environment.isComcastSetTopBox()) {
					return this.initComcastAuth().catch(() => this.initDefaultAuth());
				}

				return this.initDefaultAuth();
			})
			.then(() => this.authorize())
			.then(() => {
				if (this.cognitoCheckDisabled) {
					return Promise.resolve();
				}

				return this.syncCognitoId().catch((error) =>
					logger.error(`[CheckHwpTvId]`, { error }),
				);
			})
			.then(() => this.onReadyResolve())
			.catch((error) => {
				logger.error(`[Initialize Failed]`, { error });
				Analytics.sendEvent('AppAuthManager-Error', {
					status: `Initialize Failed`,
					error: `${error}`,
				});
				this.onReadyReject(error);
			})
			.finally(() => this.isReady());
	}

	/**
	 * Logs in the user by overriding the authorization header with the provided overrideId,
	 * and optionally sets the session header if a session parameter is present in the URL.
	 * Then calls the authorize method to complete the login process.
	 *
	 * @param {string} overrideId - The override ID to use for the authorization header.
	 * @returns {Promise} - A promise that resolves when the login process is complete.
	 */
	initOverrideAuth(overrideId) {
		logger.log(`[${this.initOverrideAuth.name}]`, { overrideId });

		logger.info(`[OVERRIDE ID: ${overrideId}]`);
		HwpTvApi.headers.Authorization = overrideId;

		const session = getSearchParam('session'); // Session passed in to reuse (typically from hwpHub)
		if (session) {
			HwpTvApi.headers.session = session;
		}

		return Promise.resolve(overrideId);
	}

	/**
	 * Logs in the user anonymously by generating a unique identifier and storing it in a cookie.
	 * The generated identifier is then used as the authorization header for the HwpTvApi.
	 * Finally, it calls the `authorize` method to complete the login process.
	 *
	 * @returns {Promise} - A promise that resolves when the login process is complete.
	 */
	initDefaultAuth() {
		logger.log(`[${this.initDefaultAuth.name}]`);

		let hwptvId = StorageManager.getCookie(ID_STORAGE_NAME);
		if (!hwptvId) {
			hwptvId = UUID();
			StorageManager.setCookie(ID_STORAGE_NAME, hwptvId);
		}
		HwpTvApi.headers.Authorization = hwptvId;

		return Promise.resolve(hwptvId);
	}

	/**
	 * Logs in the user using the Comcast MoneyBadger service. This method first checks if the MoneyBadger
	 * service is available and active, and if so, retrieves the user's information including the partnerId,
	 * householdId, and deviceHash. It then sets the appropriate headers in the HwpTvApi and calls the
	 * `authorize` method to complete the login process.
	 *
	 * @returns {Promise} - A promise that resolves when the login process is complete.
	 */
	initComcastAuth() {
		logger.log(`[${this.initComcastAuth.name}]`);

		return Comcast.getMoneyBadgerService()
			.then((bridgeService) => {
				const isActive = Comcast.isActive();

				if (bridgeService && isActive) {
					// We're ready!
					logger.debug('[MoneyBadger Ready!]');
					return Promise.resolve();
				}

				if (bridgeService && !isActive) {
					// wait for service to be ready
					logger.debug('[Wait for MoneyBadger to be ready...]');
					return new Promise((resolve) => Comcast.on('ready', resolve));
				}

				// No service available
				logger.error('[MoneyBadger Bridge Not Available]');
				Analytics.sendEvent('AppAuthManager-Error', {
					error: 'MoneyBadger bridge not available',
				});
				return Promise.reject(new Error('Bridge not available'));
			})
			.then(() =>
				Comcast.getUserInfo().then(
					(response) => {
						const { partnerId, householdId, deviceHash } = response;
						const { headers } = HwpTvApi;
						headers.platform = partnerId || headers.platform;
						headers.device = deviceHash;

						if (!householdId) {
							const error = new Error('MoneyBadger did not provide "householdId"');
							logger.warn(error.message);
							Analytics.sendEvent('AppAuthManager-Warn', {
								status: error.message,
								error: JSON.stringify({ error }),
							});
							return Promise.reject(error);
						}
						headers.Authorization = householdId;

						return householdId;
					},
					(/** @type {Error} */ error) => {
						Analytics.sendEvent('AppAuthManager-Warn', {
							status: 'MoneyBadger.getUserInfo Failed',
							error: JSON.stringify({ error }),
						});
						logger.error('[Failed to get MoneyBadger user info]', error);
						return Promise.reject(error);
					},
				),
			);
	}

	/**
	 * Checks the HWPTV ID for the current Cognito user and synchronizes it with the local identifier if necessary.
	 * This method ensures that the HWPTV ID is consistent across the application and the Cognito user profile.
	 */
	syncCognitoId() {
		logger.log(`[${this.syncCognitoId.name}]`);

		return getAuth()
			.then((Auth) => Auth.currentAuthenticatedUser())
			.then((user) => {
				if (!user) {
					return Promise.resolve();
				}

				const { attributes = {} } = user;
				const { 'custom:hwptvId': cognitoHwptvId } = attributes;
				const localHwptvId = this.getUserIdentifier();

				if (cognitoHwptvId && cognitoHwptvId !== localHwptvId) {
					// Copy cognito hwptvId to local hwptvId and re-authorize
					StorageManager.setCookie(ID_STORAGE_NAME, cognitoHwptvId);
					HwpTvApi.headers.Authorization = cognitoHwptvId;
					// Reauthorize with API after updating identifier
					logger.info({ cognitoHwptvId });
					return this.authorize();
				}

				if (!cognitoHwptvId && localHwptvId) {
					// No hwptvId set on Cognito user. Copy from local hwptvId
					Object.assign(attributes, { 'custom:hwptvId': localHwptvId });
					logger.info({ localHwptvId });
					return this.updateUserAttributes(attributes);
				}

				return Promise.resolve();
			});
	}

	/**
	 * Call to the HWP TV API to retrieve a JWT token use for easily identifying
	 * this user in the future. **Wrapped in promise to convert jquery Deferred to
	 * a native promise.
	 */
	authorize() {
		logger.log(`[${this.authorize.name}]`);

		// variant handled iff we are redirecting a user with their token
		if (HwpTvApi.headers.Authorization.search('Bearer') === -1) {
			// update our header due to new account types
			HwpTvApi.headers.Authorization = this.accountVariant
				? `${HwpTvApi.headers.Authorization}-${this.accountVariant}`
				: HwpTvApi.headers.Authorization;
		}

		return HwpTvApi.authorize().then(({ token }) => {
			if (token) {
				HwpTvApi.headers.Authorization = `Bearer ${token}`;

				// Update local uses of HWPTV ID
				const { id: hwptvId } = JWT.decode(token);
				Analytics.setUserId(hwptvId);
				StorageManager.setCookie(ID_STORAGE_NAME, hwptvId);

				return token;
			}

			const error = new Error('Missing Token!');
			logger.error(error);
			Analytics.sendEvent('AppAuthManager-Error', {
				status: `API Authorize didn't provide a token`,
				error: `${error}`,
			});
			return Promise.reject(error);
		});
	}

	/**
	 * Signs up a new user to the Cognito User Pool
	 * @param {String} username
	 */
	signUp(username) {
		logger.log(`[${this.signUp.name}]`, { username });

		const password = getRandomString();
		return getAuth()
			.then((Auth) =>
				Auth.signUp({
					username,
					password,
					attributes: {
						'custom:hwptvId': this.getUserIdentifier(),
					},
				}),
			)
			.then((user) => {
				logger.debug({ user });
				this.user = user;
				return user;
			});
	}

	/**
	 * Signs an existing user into the Cognito User Pool
	 * @param {String} username Name to use when logging into Cognito User Pool. Currently only
	 * email is supported at this time of this documentation.
	 */
	signIn(username) {
		logger.log(`[${this.signIn.name}]`, { username });

		return getAuth()
			.then((Auth) => Auth.signIn(username))
			.then((user) => {
				logger.debug({ user });
				this.user = user;
				return user;
			});
	}

	/**
	 * Signs a user out of the Cogntio User Pool
	 */
	signOut() {
		logger.log(`[${this.signOut.name}]`);

		return getAuth()
			.then((Auth) => Auth.signOut())
			.then(
				() => {
					logger.debug('[Logged Out]');
					this.user = undefined;
					HwpTvApi.headers.Authorization = '';
					StorageManager.setCookie(ID_STORAGE_NAME, '');
					return this.initialize();
				},
				(logoutError) => logger.debug({ logoutError }),
			);
	}

	/**
	 * Submits a temporary password code that was sent to the user
	 * as part of the Custom Cognito Authentication system
	 * @param {String} code	The temporary code entered by the user
	 */
	submitCode(code) {
		logger.log(`[${this.submitCode.name}]`, { code });

		return getAuth()
			.then((Auth) => Auth.sendCustomChallengeAnswer(this.user, code))
			.then((cognitoUser) => {
				if (!cognitoUser.signInUserSession) {
					const error = new Error('Invalid Code');
					return Promise.reject(error);
				}

				return this.syncCognitoId().then(() => cognitoUser);
			});
	}

	/**
	 * Gets attributes for the currently logged in Cognito user, if available.
	 */
	getUserAttributes() {
		logger.log(`[${this.getUserAttributes.name}]`);

		return getAuth()
			.then((Auth) => Auth.currentUserInfo())
			.then((response) => response?.attributes);
	}

	/**
	 * Stores given attributes to the current authenticated Cognito User
	 * @param {Object} attributes data to be stored for user
	 */
	updateUserAttributes(attributes) {
		logger.log(`[${this.updateUserAttributes.name}]`, { attributes });

		return getAuth().then(
			(Auth) =>
				Auth.currentAuthenticatedUser().then(
					(cognitoUser) => {
						logger.debug({ cognitoUser });
						if (cognitoUser) {
							return Auth.updateUserAttributes(cognitoUser, attributes);
						}

						return Promise.reject(new Error('No user to update'));
					},
					(/** @type {Error} */ error) => {
						logger.error(error);
						return Promise.reject(error);
					},
				),
			(/** @type {Error} */ error) => {
				logger.error(error);
				return Promise.reject(error);
			},
		);
	}

	/**
	 * Gets the current value (or token) from the Authorization header that is
	 * being sent too the HWP TV API.
	 */
	getIdentifier() {
		let identifier = HwpTvApi.headers.Authorization;
		if (identifier) {
			identifier = identifier.replace('Bearer ', '');
		}
		return identifier;
	}

	/**
	 * Gets the user identifier from the current authorization token.
	 * If the token can be decoded as a JWT, the user ID is extracted from the payload.
	 * Otherwise, the raw token value is returned.
	 * @returns {string} The user identifier.
	 */
	getUserIdentifier() {
		logger.log(`[${this.getUserIdentifier.name}]`);

		const identifier = this.getIdentifier();
		try {
			const payload = JWT.decode(identifier);
			return payload.id;
		} catch (error) {
			logger.error('[decode token error]', error);
		}

		return identifier;
	}

	/**
	 * Search for any user identifiers that have been supplied in the query parameters
	 */
	findUserCredentials() {
		logger.log(`[${this.findUserCredentials.name}]`);

		const queryParams = new URLSearchParams(window.location.search);

		if (queryParams.has('token')) {
			return `Bearer ${queryParams.get('token')}`;
		}

		if (queryParams.has('stbId')) {
			return queryParams.get('stbId');
		}

		return null;
	}

	/**
	 * Listener for API errors. Check token expiration and re-authorize if it's expired.
	 */
	onApiError(error) {
		logger.error(`[${this.onApiError.name}]`, error);

		const identifier = this.getIdentifier();
		if (!REGEX_UUID.test(identifier)) {
			try {
				const payload = JWT.decode(identifier);
				const expireTime = payload.exp;
				const now = Date.now().valueOf() / 1000;
				const isExpired = expireTime < now;
				logger.debug({ now, expireTime, isExpired });

				if (isExpired) {
					logger.warn('[Token timed out][Reauthorizing]');
					this.initialize();
				}
			} catch (err) {
				logger.error('[identifier is not a JWT]', err);
			}
		}
	}
}

export default new AppAuthManager();
